import React, { useState, useEffect } from 'react';
import { 
  Users, Car, ShieldCheck, AlertTriangle, FileSignature, 
  Package, Phone, Bell, HandHeart, Sun, CloudSun, CloudFog, CloudRain, CloudSnow, CloudLightning
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import styles from './SecurityDashboard.module.css';
import api from '../../api/axios';

const SecurityDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({
    visitors: [],
    vehicles: [],
    incidents: [],
    patrols: [],
    deliveries: []
  });
  const [user, setUser] = useState({ fullName: 'Security Guard' });
  const [loading, setLoading] = useState(true);
  const [sosAlert, setSosAlert] = useState(null);
  const [gates, setGates] = useState([]);
  const [weather, setWeather] = useState({ temp: 32, condition: 'Partly Cloudy', city: 'Indore, India', icon: <Sun size={18} /> });

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const lat = 22.7196;
        const lon = 75.8577;
        const city = 'Indore';
        const country = 'India';

        const weatherRes = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current_weather=true`);
        const weatherData = await weatherRes.json();
        
        const temp = Math.round(weatherData.current_weather.temperature);
        const code = weatherData.current_weather.weathercode;
        
        let condition = 'Clear';
        let icon = <Sun size={32} />;
        
        if (code === 0) { condition = 'Clear sky'; icon = <Sun size={18} />; }
        else if (code === 1 || code === 2 || code === 3) { condition = 'Partly cloudy'; icon = <CloudSun size={18} />; }
        else if (code === 45 || code === 48) { condition = 'Fog'; icon = <CloudFog size={18} />; }
        else if (code >= 51 && code <= 67) { condition = 'Rain'; icon = <CloudRain size={18} />; }
        else if (code >= 71 && code <= 82) { condition = 'Snow'; icon = <CloudSnow size={18} />; }
        else if (code >= 95) { condition = 'Thunderstorm'; icon = <CloudLightning size={18} />; }
        
        setWeather({ temp, condition, city: `${city}, ${country}`, icon });
      } catch (err) {
        console.error("Weather fetch failed", err);
      }
    };
    
    fetchWeather();
  }, []);

  useEffect(() => {
    // Setup Socket.IO listener for real-time SOS alerts and gate updates
    const socket = io(import.meta.env.VITE_API_URL.replace(/\/api\/?$/, ''), {
      transports: ['websocket'],
    });

    socket.on('new_sos', (incident) => {
      setSosAlert(incident);
      // Automatically hide alert after 10 seconds (or handle manually)
      setTimeout(() => setSosAlert(null), 10000);
    });

    socket.on('gate_updated', (updatedGate) => {
      setGates(prevGates => prevGates.map(g => g._id === updatedGate._id ? updatedGate : g));
    });

    return () => socket.disconnect();
  }, []);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        if (userInfo && userInfo.fullName) {
          setUser(userInfo);
        }

        const [visitorsRes, vehiclesRes, incidentsRes, patrolsRes, deliveriesRes, gatesRes] = await Promise.all([
          api.get('/security/visitors'),
          api.get('/security/vehicles'),
          api.get('/security/incidents'),
          api.get('/security/patrols'),
          api.get('/security/deliveries'),
          api.get('/security/gates')
        ]);

        setData({
          visitors: visitorsRes.data || [],
          vehicles: vehiclesRes.data || [],
          incidents: incidentsRes.data || [],
          patrols: patrolsRes.data || [],
          deliveries: deliveriesRes.data || []
        });

        if (gatesRes && gatesRes.data) {
          setGates(gatesRes.data);
        }
        setLoading(false);
      } catch (error) {
        console.error("Failed to fetch security dashboard data:", error);
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const today = new Date().toLocaleDateString('en-GB');

  // Filter for today's data
  const isToday = (dateString) => {
    if (!dateString) return false;
    return new Date(dateString).toLocaleDateString('en-GB') === today;
  };

  const todaysVisitors = data.visitors.filter(v => isToday(v.entryTime) || isToday(v.createdAt));
  const todaysVehicles = data.vehicles.filter(v => isToday(v.entryTime) || isToday(v.createdAt));
  const todaysIncidents = data.incidents.filter(i => isToday(i.createdAt));
  const todaysDeliveries = data.deliveries.filter(d => isToday(d.entryTime) || isToday(d.createdAt));
  const todaysPatrols = data.patrols.filter(p => isToday(p.createdAt));

  const stats = [
    { title: "Today's Visitors", value: todaysVisitors.length < 10 ? `0${todaysVisitors.length}` : todaysVisitors.length, icon: Users, type: 'blue', action: 'View all', link: '/security/visitors' },
    { title: "Today's Vehicles", value: todaysVehicles.length < 10 ? `0${todaysVehicles.length}` : todaysVehicles.length, icon: Car, type: 'blue', action: 'View all', link: '/security/vehicles' },
    { title: "Today's Deliveries", value: todaysDeliveries.length < 10 ? `0${todaysDeliveries.length}` : todaysDeliveries.length, icon: Package, type: 'blue', action: 'View all', link: '/security/deliveries' },
    { title: "Today's Incidents", value: todaysIncidents.length < 10 ? `0${todaysIncidents.length}` : todaysIncidents.length, icon: AlertTriangle, type: 'red', action: 'View all', link: '/security/incidents' },
  ];


  const targetPatrols = 4; // Daily Target
  const completedPatrols = todaysPatrols.filter(p => p.status === 'Completed').length;
  const patrolProgress = Math.min(100, Math.round((completedPatrols / targetPatrols) * 100));

  // Helper to format time
  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const firstName = user.fullName ? user.fullName.split(" ")[0] : "Guard";
  const initials = user.fullName ? user.fullName.split(" ").map((n) => n[0]).join("").substring(0, 2).toUpperCase() : "G";

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    if (hour < 21) return 'Good Evening';
    return 'Good Night';
  };

  const getImageUrl = (url) => {
    if (!url) return 'https://cdn-icons-png.flaticon.com/512/3135/3135715.png';
    if (url.startsWith('http')) return url;
    return `${import.meta.env.VITE_API_URL.replace(/\/api\/?$/, '')}${url}`;
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading Security Dashboard...</div>;
  }

  return (
    <div className={styles.dashboard}>
      <main className={styles.mainContent}>
        {/* Welcome Banner */}
        <div className={styles.welcomeBanner}>
          <div className={styles.bannerContent}>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '0 0 8px 0' }}>
              {getGreeting()}, {firstName}! <HandHeart size={28} color="#f59e0b" />
            </h2>
            <p>Welcome back to the Security Dashboard. Monitor the society's safety and logs.</p>
            <div className={styles.weatherInfo}>
              <span>{weather.icon} {weather.temp}°C • {weather.condition} • {weather.city}</span>
            </div>
          </div>
          <div className={styles.bannerImage}>
            {/* Optional: Add an illustration here later if needed */}
          </div>
        </div>

        {/* STATS ROW */}
        <div className={styles.statsGrid}>
          {stats.map((stat, idx) => {
            const Icon = stat.icon;
            return (
              <div key={idx} className={styles.statCard}>
                <div className={styles.statTop}>
                  <span className={styles.statTitle}>{stat.title}</span>
                  <div className={`${styles.statIcon} ${styles[stat.type]}`}>
                    <Icon size={16} />
                  </div>
                </div>
                <div className={styles.statBottom}>
                  <strong>{stat.value}</strong>
                  <button className={styles.viewLink} onClick={() => navigate(stat.link)}>{stat.action}</button>
                </div>
              </div>
            );
          })}
        </div>

        {/* MIDDLE ROW */}
        <div className={styles.middleGrid}>
          {/* Visitor Log */}
          <div className={`${styles.card} ${styles.visitorCard}`}>
            <div className={styles.cardHeader}>
              <h3>Visitor Log (Today)</h3>
              <button className={styles.viewAll} onClick={() => navigate('/security/visitors')}>View All</button>
            </div>
            <div className={styles.table}>
              <div className={styles.tableHead}>
                <span>Visitor Name</span>
                <span>Flat / Unit</span>
                <span>Time</span>
                <span>Status</span>
              </div>
              {todaysVisitors.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>No visitors today</div>
              ) : todaysVisitors.slice(0, 4).map((v, i) => (
                <div key={i} className={styles.tableRow}>
                  <strong data-label="Visitor Name">{v.name}</strong>
                  <span data-label="Flat / Unit">{v.flatNumber ? `${v.wing || ''}-${v.flatNumber}` : 'N/A'}</span>
                  <span data-label="Time">{formatTime(v.entryTime || v.createdAt)}</span>
                  <span data-label="Status" className={styles.badgeIn}>{v.status || 'In'}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Gate Overview */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3>Gate Overview</h3>
            </div>
            <div className={styles.gateList}>
              {gates.map((g, i) => (
                <div key={i} className={styles.gateItem}>
                  <span>{g.name}</span>
                  <span className={`${styles.gateBadge} ${g.status === 'Open' ? styles.green : styles.red}`}>{g.status}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Patrol Status */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3>Patrol Status (Today)</h3>
            </div>
            <div className={styles.patrolContent}>
              <div className={styles.circularChart}>
                <svg viewBox="0 0 36 36" className={styles.svgCircle}>
                  <path className={styles.circleBg} d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                  <path className={styles.circleProgress} strokeDasharray={`${patrolProgress}, 100`} d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                </svg>
                <div className={styles.patrolText}>
                  <strong>{patrolProgress}%</strong>
                  <span>Completed</span>
                </div>
              </div>
              <div className={styles.patrolSubtext}>
                <strong>{completedPatrols} / {targetPatrols}</strong>
                <span>Rounds Completed</span>
              </div>
            </div>
          </div>
        </div>

        {/* BOTTOM ROW */}
        <div className={styles.bottomGrid}>
          {/* Quick Actions */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3>Quick Actions</h3>
            </div>
            <div className={styles.quickActionsGrid}>
              <button className={styles.qaBtn} onClick={() => navigate('/security/gatepass')}>
                <span className={styles.qaIcon}><FileSignature size={14} color="#3b82f6" /></span> New Gate Pass
              </button>
              <button className={styles.qaBtn} onClick={() => navigate('/security/incidents')}>
                <span className={styles.qaIcon}><AlertTriangle size={14} color="#3b82f6" /></span> Report Incident
              </button>
              <button className={styles.qaBtn} onClick={() => navigate('/security/deliveries')}>
                <span className={styles.qaIcon}><Package size={14} color="#3b82f6" /></span> Add Delivery
              </button>
              <button className={`${styles.qaBtn} ${styles.qaEmergency}`} onClick={() => navigate('/security/emergency')}>
                <span className={`${styles.qaIcon} ${styles.qaIconRed}`}><Phone size={14} color="#ef4444" /></span> Emergency Call
              </button>
            </div>
          </div>

          {/* Recent Incidents */}
          <div className={`${styles.card} ${styles.incidentCard}`}>
            <div className={styles.cardHeader}>
              <h3>Recent Incidents</h3>
              <button className={styles.viewAll} onClick={() => navigate('/security/incidents')}>View All</button>
            </div>
            <div className={styles.list}>
              {data.incidents.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>No recent incidents</div>
              ) : data.incidents.slice(0, 3).map((inc, i) => (
                <div key={i} className={styles.incidentItem}>
                  <div className={styles.listIconIncident}><AlertTriangle size={14}/></div>
                  <div className={styles.listText}>
                    <h4>{inc.title}</h4>
                    <p>{formatTime(inc.createdAt)}</p>
                  </div>
                  <span className={styles.badgePending}>{inc.status}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SecurityDashboard;
